import type { NetWorthChangePeriod } from '@/interfaces/netWorthChanges';
import type {
  WealthTrendDetail,
  WealthTrendDetailRequest,
  WealthTrendPoint,
  WealthTrendResponse,
  WealthTrendSeries,
} from '@/interfaces/wealthReportedTrend';
import { toApiDateParam } from '@/utils/date';
import { advisorInvestorsApi } from './advisorInvestorsApi';

/** A series as the API may send it: `data` can be missing. */
type RawWealthTrendSeries = Omit<WealthTrendSeries, 'data'> & {
  data?: WealthTrendPoint[] | null;
};

/** The trend response as the API may send it. */
interface RawWealthTrendResponse {
  Records?: RawWealthTrendSeries[] | null;
  TotalLength?: number;
}

/**
 * Reads the trend response. An advisor with no balances in the range gets an
 * empty response with no `Records`, and a series can arrive without `data`, so
 * both fall back to empty rather than trusting their presence.
 */
export function normalizeWealthTrend(
  response: RawWealthTrendResponse | null | undefined,
): WealthTrendResponse {
  const records: WealthTrendSeries[] = (response?.Records ?? []).map(
    (series) => ({ ...series, data: series.data ?? [] }),
  );
  return {
    Records: records,
    TotalLength: response?.TotalLength ?? records.length,
  };
}

/** Reads the details response, defaulting the lists it may leave out. */
export function normalizeWealthTrendDetail(
  response: Partial<WealthTrendDetail> | null | undefined,
): WealthTrendDetail {
  return {
    Trends: response?.Trends ?? [],
    Changes: response?.Changes ?? [],
    BiggestChangeType: response?.BiggestChangeType ?? null,
  };
}

export const advisorWealthTrendApi = advisorInvestorsApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * The advisor's managed and held-away totals per day over the range. The
     * advisor comes from the session, so the totals are scoped server-side to
     * the clients they can see.
     */
    getWealthReportedTrend: builder.query<
      WealthTrendResponse,
      NetWorthChangePeriod
    >({
      queryFn: async ({ startDate, endDate }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: '/api/advisor/HomeHistoricalTrend',
          method: 'GET',
          params: {
            startDate: toApiDateParam(startDate),
            endDate: toApiDateParam(endDate),
          },
        });
        if (result.error) return { error: result.error };
        return {
          data: normalizeWealthTrend(
            result.data as RawWealthTrendResponse | null,
          ),
        };
      },
    }),

    /**
     * What the popover shows for one point: the series' value at the start
     * of its window, and the clients whose balance moved most that day.
     */
    getWealthReportedTrendDetail: builder.query<
      WealthTrendDetail,
      WealthTrendDetailRequest
    >({
      queryFn: async (
        { selectedDate, comparisonDate, isHome },
        _api,
        _extra,
        baseQuery,
      ) => {
        const result = await baseQuery({
          url: '/api/advisor/HomeHistoricalTrendDetails',
          method: 'GET',
          params: {
            selectedDate: toApiDateParam(selectedDate),
            comparisonDate: toApiDateParam(comparisonDate),
            isHome,
          },
        });
        if (result.error) return { error: result.error };
        return {
          data: normalizeWealthTrendDetail(
            result.data as Partial<WealthTrendDetail> | null,
          ),
        };
      },
    }),
  }),
});

export const {
  useGetWealthReportedTrendQuery,
  useGetWealthReportedTrendDetailQuery,
} = advisorWealthTrendApi;
